import { motion } from 'framer-motion';
import {
  ArrowLeft,
  Calendar,
  Check,
  Clock,
  Copy,
  Facebook,
  Instagram,
  Linkedin,
  Tag,
  User,
  Youtube,
} from 'lucide-react';
import React, { useEffect, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';

import SectionRenderer from '../components/Blog/SectionRenderer';
import Toc from '../components/Blog/sectionRenderers/Toc';
import MarketingPageFooter from '../components/marketing/MarketingPageFooter';
import MarketingLandingNav, { MARKETING_NAV_TOP_OFFSET } from '../components/marketing/MarketingLandingNav';
import { supabase } from '../lib/supabase';
import { SEO } from '../shared/components/SEO';
import { buildBreadcrumbJsonLd } from '../shared/seo/breadcrumbJsonLd';
import type { BlogPost, BlogSection, TocData } from '../types/blog';
import { resolveBlogImageSrc } from '../config/blogImagePaths';
import { placeMidSubscribeCta } from '../utils/blogSectionLayout';

type PrerenderBlogCache = Record<string, { post: BlogPost; sections: BlogSection[] }>;

const SITE_URL = 'https://www.gograbjob.com';
const DEFAULT_OG = '/images/og-default.png';

// ── GoGrabJob social profile links (with brand colours) ──────────────────────
const GGJ_SOCIALS = [
  { href: 'https://www.linkedin.com/company/gograbjobofficial/', Icon: Linkedin,  label: 'LinkedIn',  color: '#0077B5' },
  { href: 'https://www.instagram.com/gograbjob_ai/',             Icon: Instagram, label: 'Instagram', color: '#E1306C' },
  { href: 'https://www.facebook.com/profile.php?id=61579650282244', Icon: Facebook, label: 'Facebook', color: '#1877F2' },
  { href: 'https://www.youtube.com/@GoGrabJob',                  Icon: Youtube,   label: 'YouTube',   color: '#FF0000' },
] as const;

/** W3.4 — crawlable pricing + signup CTAs at end of articles. */
function BlogPostSeoCta() {
  return (
    <div className="blog-post-cta">
      <h2 className="blog-post-cta__title">
        Run your job search in one workspace
      </h2>
      <p className="blog-post-cta__text">
        Find roles ranked to your profile, tailor your existing resume for each job, score ATS fit,
        and track applications — free to start.
      </p>
      <div className="flex flex-wrap gap-3">
        <a
          href="/signup"
          className="home-marketing-cta text-sm font-medium"
        >
          Start free
        </a>
        <a
          href="/pricing"
          className="blog-post-cta__btn blog-post-cta__btn--secondary"
        >
          View pricing
        </a>
      </div>
    </div>
  );
}

// ── Schema ────────────────────────────────────────────────────────────────────

function buildArticleSchema(post: BlogPost): Record<string, unknown> {
  const url = `${SITE_URL}/blog/${post.slug}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.meta_description ?? post.hero_intro ?? undefined,
    image: resolveBlogImageSrc(post.featured_image, post.slug) ?? `${SITE_URL}${DEFAULT_OG}`,
    mainEntityOfPage: { '@type': 'WebPage', '@id': url },
    url,
    datePublished: post.published_at ?? post.created_at,
    dateModified: post.updated_at ?? post.published_at ?? post.created_at,
    author: post.author_name
      ? { '@type': 'Person', name: post.author_name }
      : { '@type': 'Organization', name: 'GoGrabJob' },
    publisher: {
      '@type': 'Organization',
      name: 'GoGrabJob',
      logo: { '@type': 'ImageObject', url: `${SITE_URL}/ggj-logo-192.png` },
    },
  };
}

// ── Social icon strip (reusable for vertical sidebar + horizontal inline) ────

interface ShareSidebarProps {
  url: string;
  title: string;
}

/** Vertical strip used inside the sidebar layout columns. */
const SocialStripVertical: React.FC<ShareSidebarProps> = ({ url }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="hidden lg:flex flex-col items-center gap-3 sticky top-24">
      {GGJ_SOCIALS.map(({ href, Icon, label, color }) => (
        <a
          key={label}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          title={label}
          className="flex items-center justify-center w-10 h-10 rounded-full text-white shadow-sm transition-transform hover:scale-110"
          style={{ background: color }}
        >
          <Icon className="w-5 h-5" />
        </a>
      ))}

      {/* Copy-link share button — brand primary colour */}
      <div className="w-6 border-t border-white/20 my-0.5" />
      <button
        type="button"
        onClick={copy}
        title={copied ? 'Copied!' : 'Copy link'}
        className="flex items-center justify-center w-10 h-10 rounded-full text-white shadow-sm transition-transform hover:scale-110"
        style={{ background: copied ? '#22C55E' : 'var(--vscode-primary)' }}
      >
        {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
      </button>
    </div>
  );
};

/** Horizontal strip — rendered inline below the article header for top/no-TOC layout. */
const SocialStripHorizontal: React.FC<ShareSidebarProps> = ({ url }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex items-center gap-3 my-6">
      {GGJ_SOCIALS.map(({ href, Icon, label, color }) => (
        <a
          key={label}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          title={label}
          className="flex items-center justify-center w-10 h-10 rounded-full text-white shadow-sm transition-transform hover:scale-110"
          style={{ background: color }}
        >
          <Icon className="w-5 h-5" />
        </a>
      ))}
      <div className="h-8 w-px vscode-border border-l mx-1" />
      <button
        type="button"
        onClick={copy}
        title={copied ? 'Copied!' : 'Copy link'}
        className="flex items-center justify-center w-10 h-10 rounded-full text-white shadow-sm transition-transform hover:scale-110"
        style={{ background: copied ? '#22C55E' : 'var(--vscode-primary)' }}
      >
        {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
      </button>
    </div>
  );
};

// ── Mobile social bar (shown at bottom of post on small screens) ─────────────

const MobileShareBar: React.FC<ShareSidebarProps> = ({ url }) => {
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); } catch { /* ignore */ }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="flex lg:hidden items-center gap-3 flex-wrap py-6 border-t vscode-border mt-4">
      {GGJ_SOCIALS.map(({ href, Icon, label, color }) => (
        <a
          key={label}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          title={label}
          className="flex items-center justify-center w-10 h-10 rounded-full text-white shadow-sm"
          style={{ background: color }}
        >
          <Icon className="w-5 h-5" />
        </a>
      ))}
      <div className="h-8 w-px vscode-border border-l mx-1" />
      <button
        type="button"
        onClick={copy}
        title={copied ? 'Copied!' : 'Copy link'}
        className="flex items-center justify-center w-10 h-10 rounded-full text-white shadow-sm"
        style={{ background: copied ? '#22C55E' : 'var(--vscode-primary)' }}
      >
        {copied ? <Check className="w-5 h-5" /> : <Copy className="w-5 h-5" />}
      </button>
    </div>
  );
};

// ── Sticky TOC sidebar ─────────────────────────────────────────────────────────

interface TocSidebarProps {
  section: BlogSection;
  activeIndex: number;
}

const TocSidebar: React.FC<TocSidebarProps> = ({ section, activeIndex }) => {
  const tocData = section.data as TocData;
  const items = tocData.items ?? [];
  const label = section.heading?.trim() || 'In this article';

  if (items.length === 0) return null;

  return (
    <nav
      aria-label="Table of contents"
      className="sticky top-24 rounded-xl vscode-bg-secondary border vscode-border p-5"
    >
      <p className="blog-toc__label text-xs font-bold uppercase tracking-widest mb-5">{label}</p>
      <ol className="list-none m-0 p-0 space-y-0">
        {items.map((item, i) => (
          <li
            key={i}
            className={`flex items-start gap-2.5 py-2 border-b last:border-b-0 transition-colors ${
              activeIndex === i ? 'border-[var(--vscode-primary)]/20' : 'vscode-border'
            }`}
          >
            <span
              className={`text-xs shrink-0 tabular-nums w-4 text-right mt-0.5 transition-colors ${
                activeIndex === i ? 'text-[var(--vscode-primary)] font-bold' : 'vscode-text-secondary'
              }`}
            >
              {i + 1}.
            </span>
            <a
              href={`#toc-section-${i}`}
              onClick={(e) => {
                const el = document.getElementById(`toc-section-${i}`);
                if (el) { e.preventDefault(); el.scrollIntoView({ behavior: 'smooth', block: 'start' }); }
              }}
              className={`text-sm leading-snug no-underline transition-colors ${
                activeIndex === i
                  ? 'text-[var(--vscode-primary)] font-medium'
                  : 'vscode-text-primary hover:text-[var(--vscode-primary)]'
              }`}
            >
              {item}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  );
};

// ── Fetch state ────────────────────────────────────────────────────────────────

type FetchState =
  | { status: 'loading' }
  | { status: 'not-found' }
  | { status: 'error'; message: string }
  | { status: 'ok'; post: BlogPost; sections: BlogSection[] };

// ── Main component ─────────────────────────────────────────────────────────────

interface BlogPostPageProps {
  onNavigate?: (view: string) => void;
}

const BlogPostPage: React.FC<BlogPostPageProps> = ({ onNavigate }) => {
  const rawSlug = window.location.pathname.replace(/^\/blog\//, '').split('?')[0];
  const slug = decodeURIComponent(rawSlug);

  const searchParams = new URLSearchParams(window.location.search);
  const isPreview = searchParams.get('preview') === '1';
  const previewToken = searchParams.get('t') ?? '';

  const [state, setState] = useState<FetchState>({ status: 'loading' });
  const [activeIdx, setActiveIdx] = useState(0);
  const sectionsRef = useRef<HTMLElement[]>([]);

  // ── Scroll spy for TOC active item ─────────────────────────────────────────
  useEffect(() => {
    const handleScroll = () => {
      const sections = sectionsRef.current;
      if (!sections.length) return;
      const threshold = window.innerHeight * 0.35;
      let found = 0;
      for (let i = 0; i < sections.length; i++) {
        const rect = sections[i].getBoundingClientRect();
        if (rect.top < threshold) found = i;
      }
      setActiveIdx(found);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // ── Fetch ──────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!slug) { setState({ status: 'not-found' }); return; }

    const fetchPost = async () => {
      setState({ status: 'loading' });
      try {
        const isPrerender =
          typeof window !== 'undefined' &&
          (window as { __GGJ_PRERENDER__?: boolean }).__GGJ_PRERENDER__;
        if (isPrerender && !isPreview) {
          const { default: cache } = await import('../generated/prerenderBlogData.json');
          const cached = (cache as PrerenderBlogCache)[slug];
          if (cached) {
            setState({ status: 'ok', post: cached.post, sections: cached.sections });
            return;
          }
          // Build-time cache is authoritative — avoid live Supabase fetch in Puppeteer.
          setState({ status: 'not-found' });
          return;
        }

        let post: BlogPost | null = null;
        let sections: BlogSection[] = [];

        if (isPreview && previewToken) {
          const fnUrl = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/blog-preview-token?slug=${encodeURIComponent(slug)}&t=${encodeURIComponent(previewToken)}`;
          const resp = await fetch(fnUrl);
          if (resp.status === 404) { setState({ status: 'not-found' }); return; }
          if (!resp.ok) throw new Error(`Preview token error: ${resp.status}`);
          const body = await resp.json() as { post: BlogPost; sections: BlogSection[] };
          post = body.post;
          sections = body.sections;
        } else {
          const { data: postData, error: postErr } = await supabase
            .from('blog_posts')
            .select('*')
            .eq('slug', slug)
            .eq('status', 'published')
            .single();

          if (postErr?.code === 'PGRST116') { setState({ status: 'not-found' }); return; }
          if (postErr) throw postErr;
          post = postData as BlogPost;

          const { data: secData, error: secErr } = await supabase
            .from('blog_sections')
            .select('*')
            .eq('post_id', post.id)
            .order('position', { ascending: true });

          if (secErr) throw secErr;
          sections = (secData ?? []) as BlogSection[];
        }

        if (!post) { setState({ status: 'not-found' }); return; }
        setState({ status: 'ok', post, sections });
      } catch (err) {
        console.error('[BlogPostPage] fetch error:', err);
        setState({ status: 'error', message: 'Failed to load this post. Please try again.' });
      }
    };

    fetchPost();
  }, [slug, isPreview, previewToken]);

  // Puppeteer prerender: wait for Supabase fetch — App.tsx skips early signal on /blog/:slug.
  const prerenderSignaledRef = useRef(false);
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (!(window as { __GGJ_PRERENDER__?: boolean }).__GGJ_PRERENDER__) return;
    if (state.status !== 'ok' && state.status !== 'not-found' && state.status !== 'error') return;
    if (prerenderSignaledRef.current) return;
    prerenderSignaledRef.current = true;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        document.dispatchEvent(new Event('ggj-prerender-ready'));
      });
    });
  }, [state.status]);

  // ── Loading ────────────────────────────────────────────────────────────────
  if (state.status === 'loading') {
    return (
      <div className="blog-marketing-page relative min-h-screen flex flex-1 flex-col w-full">
        <MarketingLandingNav onNavigate={onNavigate} activeView="blog" variant="marketing" />
        <div className={`flex-1 flex items-center justify-center ${MARKETING_NAV_TOP_OFFSET}`}>
          <div className="w-10 h-10 rounded-full border-2 border-[var(--blog-link)]/20 border-t-[var(--blog-link)] animate-spin" />
        </div>
      </div>
    );
  }

  // ── Not found ──────────────────────────────────────────────────────────────
  if (state.status === 'not-found') {
    return (
      <div className="blog-marketing-page relative min-h-screen flex flex-1 flex-col w-full">
        <SEO
          title="Post Not Found — GoGrabJob Blog"
          description="This blog post doesn't exist or hasn't been published yet."
          canonical={`${SITE_URL}/blog/${slug}`}
          noIndex
        />
        <MarketingLandingNav onNavigate={onNavigate} activeView="blog" variant="marketing" />
        <div className={`flex-1 flex flex-col items-center justify-center px-4 py-20 text-center ${MARKETING_NAV_TOP_OFFSET}`}>
          <h1 className="text-6xl font-bold mb-4" style={{ color: 'var(--blog-link)' }}>404</h1>
          <h2 className="blog-post-title mb-3">Post Not Found</h2>
          <p className="blog-post-intro mb-8 max-w-md">This post doesn&apos;t exist or hasn&apos;t been published yet.</p>
          <a href="/blog" className="blog-post-back">
            <ArrowLeft className="w-4 h-4" />
            Back to Blog
          </a>
        </div>
        <MarketingPageFooter onNavigate={onNavigate} theme="blog" />
      </div>
    );
  }

  // ── Error ──────────────────────────────────────────────────────────────────
  if (state.status === 'error') {
    return (
      <div className="blog-marketing-page relative min-h-screen flex flex-1 flex-col w-full">
        <MarketingLandingNav onNavigate={onNavigate} activeView="blog" variant="marketing" />
        <div className={`flex-1 flex flex-col items-center justify-center px-4 py-20 text-center ${MARKETING_NAV_TOP_OFFSET}`}>
          <p className="text-red-400 mb-4">{state.message}</p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="blog-post-back"
          >
            Try again
          </button>
        </div>
        <MarketingPageFooter onNavigate={onNavigate} theme="blog" />
      </div>
    );
  }

  // ── Render post ────────────────────────────────────────────────────────────
  const { post, sections } = state;
  const canonicalUrl = `${SITE_URL}/blog/${post.slug}`;
  const ogImage = resolveBlogImageSrc(post.featured_image, post.slug) ?? `${SITE_URL}${DEFAULT_OG}`;
  const heroImage = resolveBlogImageSrc(post.featured_image, post.slug);
  // meta_title from the LLM blog generator already ends with "| GoGrabJob"
  // (enforced in blogGenerationPrompt). Only append the brand when it's missing,
  // otherwise titles render as "… | GoGrabJob | GoGrabJob".
  const baseTitle = (post.meta_title ?? post.title).trim();
  const seoTitle = /\|\s*GoGrabJob\s*$/i.test(baseTitle)
    ? baseTitle
    : `${baseTitle} | GoGrabJob`;
  const shareUrl = canonicalUrl;

  // Identify TOC section and its alignment
  const tocSection = sections.find((s) => s.type === 'toc');
  const tocAlign = tocSection ? ((tocSection.data as TocData).align ?? 'top') : null;
  const hasSidebar = tocAlign === 'left' || tocAlign === 'right';

  // Non-TOC sections to render in the body (TOC is pulled out when it's a sidebar)
  const bodySections = placeMidSubscribeCta(
    hasSidebar ? sections.filter((s) => s.type !== 'toc') : sections,
  );

  // Assign IDs for scroll-spy.  Only non-toc sections with headings get an ID
  // so the counter matches the TOC items array index (0-based, toc-section-0…).
  let sectionCounter = 0;
  const sectionIdsMap = new Map<string, number>();
  for (const s of bodySections) {
    if (s.heading && s.type !== 'toc') sectionIdsMap.set(s.id, sectionCounter++);
  }

  return (
    <div className="blog-marketing-page relative min-h-screen flex flex-1 flex-col w-full">
      <SEO
        title={seoTitle}
        description={post.meta_description ?? post.hero_intro ?? post.title}
        canonical={canonicalUrl}
        ogImage={ogImage}
        ogType="article"
        articlePublishedTime={post.published_at ?? post.created_at}
        articleModifiedTime={post.updated_at ?? post.published_at ?? post.created_at}
        articleAuthor={post.author_name ?? 'GoGrabJob'}
        jsonLd={[
          buildArticleSchema(post),
          buildBreadcrumbJsonLd([
            { name: 'Home', path: '/' },
            { name: 'Blog', path: '/blog' },
            { name: post.title },
          ]),
        ]}
      />

      {post.keywords && post.keywords.length > 0 && (
        <Helmet>
          <meta name="keywords" content={post.keywords.join(', ')} />
        </Helmet>
      )}

      {isPreview && (
        <div className="fixed top-0 left-0 right-0 z-50 bg-amber-500 text-black text-center text-sm font-semibold py-2 px-4">
          Preview mode — this post is not published
        </div>
      )}

      <MarketingLandingNav onNavigate={onNavigate} activeView="blog" variant="marketing" />

      <main className={`blog-post-main ${isPreview ? 'pt-[calc(3.75rem+2.5rem+env(safe-area-inset-top,0px))]' : MARKETING_NAV_TOP_OFFSET}`}>
        <div className="blog-post-layout">
          {heroImage && (
            <div className="blog-post-hero">
              <img
                key={post.featured_image ?? post.slug}
                src={heroImage}
                alt={post.title}
                className="blog-post-hero__img"
                loading="eager"
                fetchPriority="high"
              />
            </div>
          )}

          <motion.header
            className="blog-post-header"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <a href="/blog" className="blog-post-back">
              <ArrowLeft className="w-4 h-4" />
              Back to Blog
            </a>

            <div className="flex flex-wrap items-center gap-2 mb-4">
              {post.category && (
                <span className="blog-post-category">
                  <Tag className="w-3 h-3" />
                  {post.category}
                </span>
              )}
              {isPreview && (
                <span className="bg-amber-500/20 text-amber-300 px-3 py-1 rounded-full text-sm border border-amber-500/30 capitalize">
                  {post.status}
                </span>
              )}
            </div>

            <h1 className="blog-post-title">
              {post.hero_headline ?? post.title}
            </h1>

            {post.hero_intro && (
              <p className="blog-post-intro">{post.hero_intro}</p>
            )}

            <div className="blog-post-meta">
              {post.author_name && (
                <div className="flex items-center gap-1.5">
                  <User className="w-4 h-4" />
                  <span>{post.author_name}</span>
                </div>
              )}
              {post.published_at && (
                <div className="flex items-center gap-1.5">
                  <Calendar className="w-4 h-4" />
                  <time dateTime={post.published_at}>
                    {new Date(post.published_at).toLocaleDateString('en-IN', {
                      day: 'numeric', month: 'long', year: 'numeric',
                    })}
                  </time>
                </div>
              )}
              {post.read_time_min && (
                <div className="flex items-center gap-1.5">
                  <Clock className="w-4 h-4" />
                  <span>{post.read_time_min} min read</span>
                </div>
              )}
            </div>
          </motion.header>
        </div>

        <article className="blog-post-body">

          {/* ── Body layout: sidebar (when TOC is left/right) or single-column ─ */}
          {hasSidebar ? (
            // Social strip is ALWAYS on the far-left column.
            // For left-sidebar:  [Social] [TOC] [Content]
            // For right-sidebar: [Social] [Content] [TOC]
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col lg:flex-row gap-6 lg:gap-8 items-start min-w-0">
              {/* Social strip — desktop sidebar only */}
              <div className="hidden lg:block shrink-0 pt-1">
                <SocialStripVertical url={shareUrl} title={post.title} />
              </div>

              {tocAlign === 'left' && tocSection && (
                <div className="hidden lg:block w-64 xl:w-72 shrink-0">
                  <TocSidebar section={tocSection} activeIndex={activeIdx} />
                </div>
              )}

              {/* Content — always flex-1 */}
              <div className="flex-1 min-w-0 w-full">
                {tocSection && (
                  <div className="lg:hidden mb-6">
                    <Toc
                      data={tocSection.data as TocData}
                      heading={tocSection.heading ?? undefined}
                    />
                  </div>
                )}

                {bodySections.map((section) => {
                  const sIdx = sectionIdsMap.get(section.id);
                  const wrapId = sIdx !== undefined ? `toc-section-${sIdx}` : undefined;

                  return section.type === 'cta' ? (
                    <div key={section.id} id={wrapId} className="py-2">
                      <SectionRenderer section={section} />
                    </div>
                  ) : (
                    <div
                      key={section.id}
                      id={wrapId}
                      ref={(el) => {
                        if (el && sIdx !== undefined) sectionsRef.current[sIdx] = el as unknown as HTMLElement;
                      }}
                    >
                      <SectionRenderer section={section} />
                    </div>
                  );
                })}

                <MobileShareBar url={shareUrl} title={post.title} />

                <BlogPostSeoCta />

                <div className="pb-12 pt-8 border-t" style={{ borderColor: 'var(--blog-card-border)' }}>
                  <a href="/blog" className="blog-post-footer-link">
                    <ArrowLeft className="w-4 h-4" />
                    Back to all articles
                  </a>
                </div>
              </div>

              {tocAlign === 'right' && tocSection && (
                <div className="hidden lg:block w-64 xl:w-72 shrink-0">
                  <TocSidebar section={tocSection} activeIndex={activeIdx} />
                </div>
              )}
            </div>
          ) : (
            /* ── Single-column (TOC align='top' or no TOC) ─────────────────── */
            <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
              {/* Inline social strip — matches editor preview position */}
              <SocialStripHorizontal url={shareUrl} title={post.title} />

              {bodySections.map((section) => {
                const sIdx = sectionIdsMap.get(section.id);
                const wrapId = sIdx !== undefined ? `toc-section-${sIdx}` : undefined;

                return section.type === 'cta' ? (
                  <div key={section.id} id={wrapId} className="py-2">
                    <SectionRenderer section={section} />
                  </div>
                ) : (
                  <div
                    key={section.id}
                    id={wrapId}
                    ref={(el) => {
                      if (el && sIdx !== undefined) sectionsRef.current[sIdx] = el as unknown as HTMLElement;
                    }}
                  >
                    <SectionRenderer section={section} />
                  </div>
                );
              })}

              <MobileShareBar url={shareUrl} title={post.title} />

              <BlogPostSeoCta />

              <div className="pb-12 pt-8 border-t" style={{ borderColor: 'var(--blog-card-border)' }}>
                <a href="/blog" className="blog-post-footer-link">
                  <ArrowLeft className="w-4 h-4" />
                  Back to all articles
                </a>
              </div>
            </div>
          )}
        </article>
      </main>

      <MarketingPageFooter onNavigate={onNavigate} theme="blog" />
    </div>
  );
};

export default BlogPostPage;
